/**
 * Toast — temporary, polite notification anchored to the bottom of the
 * viewport.
 *
 * Accessibility: the wrapper exposes `role="status"` plus `aria-live="polite"`
 * so screen readers announce the message without interrupting whatever the
 * user is doing. The toast is dismissed automatically after `durationMs`
 * (default 4000ms) by calling the `onClose` callback; consumers control the
 * `open` state. When `durationMs <= 0` the toast persists until the consumer
 * explicitly closes it — useful for the SW "new version available" prompt
 * that needs an explicit user action (Task 22.2).
 *
 * Optional action: pass `actionLabel` + `onAction` to render an inline
 * button next to the message (e.g. "Refresh" for the SW update prompt).
 *
 * Animations use Framer Motion which natively honors
 * `prefers-reduced-motion: reduce`, so users with motion sensitivity get a
 * simple opacity fade instead of the slide-up.
 */

import { useEffect } from 'react';
import type { ReactElement } from 'react';
import { AnimatePresence, motion } from 'framer-motion';

import styles from './Toast.module.css';

export type ToastTone = 'info' | 'success' | 'error';

interface ToastProps {
  /** When `true`, the toast is mounted and animated in. */
  open: boolean;
  /** Visible message text. */
  message: string;
  /** Visual tone; defaults to `'info'`. */
  tone?: ToastTone;
  /** Auto-dismiss timeout in milliseconds. Defaults to 4000ms.
   *  Pass `0` (or any non-positive value) to disable auto-dismiss. */
  durationMs?: number;
  /** Invoked when the auto-dismiss timer fires or the user dismisses. */
  onClose?: () => void;
  /** Optional inline action label (e.g. "Yenile"). */
  actionLabel?: string;
  /** Invoked when the action button is clicked. */
  onAction?: () => void;
}

const TONE_CLASS: Record<ToastTone, string> = {
  info: styles.info ?? 'toast--info',
  success: styles.success ?? 'toast--success',
  error: styles.error ?? 'toast--error',
};

export function Toast({
  open,
  message,
  tone = 'info',
  durationMs = 4000,
  onClose,
  actionLabel,
  onAction,
}: ToastProps): ReactElement {
  // Auto-dismiss after `durationMs` while the toast is open. The dependency
  // list intentionally includes `onClose` so a swapped handler is honored on
  // the next tick rather than firing the stale one.
  useEffect(() => {
    if (!open) return;
    if (durationMs <= 0) return;
    const id = window.setTimeout(() => {
      onClose?.();
    }, durationMs);
    return () => {
      window.clearTimeout(id);
    };
  }, [open, durationMs, onClose]);

  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          key="toast"
          role="status"
          aria-live="polite"
          aria-atomic="true"
          className={[styles.toast, TONE_CLASS[tone] ?? ''].filter(Boolean).join(' ')}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.2 }}
          data-testid="toast"
          data-tone={tone}
        >
          <span className={styles.message}>{message}</span>
          {actionLabel !== undefined && onAction !== undefined ? (
            <button
              type="button"
              className={styles.actionBtn}
              onClick={() => {
                onAction();
                onClose?.();
              }}
            >
              {actionLabel}
            </button>
          ) : null}
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
